import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Sparkles, Plus, CheckCircle2, AlertCircle, Play, Loader2, BookOpen, X } from 'lucide-react';
import { api } from '@/lib/api';

const FORMULA_CATEGORIES = [
  { cat: 'Time Intelligence', fns: [
    { name: 'WTD', desc: 'Week to date', usage: 'WTD({Measure})' },
    { name: 'MTD', desc: 'Month to date', usage: 'MTD({Measure})' },
    { name: 'QTD', desc: 'Quarter to date', usage: 'QTD({Measure})' },
    { name: 'YTD', desc: 'Year to date', usage: 'YTD({Measure})' },
    { name: 'ITD', desc: 'Inception to date', usage: 'ITD({Measure})' },
    { name: 'LTD', desc: 'Life to date (= ITD)', usage: 'LTD({Measure})' },
    { name: 'PY', desc: 'Same point last year', usage: 'PY(YTD({Measure}))' },
    { name: 'PP', desc: 'Prior period', usage: 'PP(QTD({Measure}))' },
    { name: 'YOY', desc: 'Year-over-year %', usage: 'YOY(YTD({Measure}))' },
    { name: 'PRIOR_MONTH', desc: 'Value in prior month', usage: 'PRIOR_MONTH({Measure})' },
    { name: 'PRIOR_QUARTER', desc: 'Value in prior quarter', usage: 'PRIOR_QUARTER({Measure})' },
    { name: 'PRIOR_YEAR', desc: 'Value in prior year', usage: 'PRIOR_YEAR({Measure})' },
    { name: 'MOM_CHANGE', desc: 'Month-over-month change', usage: 'MOM_CHANGE({Measure})' },
    { name: 'MOM_CHANGE_PERCENT', desc: 'Month-over-month %', usage: 'MOM_CHANGE_PERCENT({Measure})' },
    { name: 'QOQ_CHANGE', desc: 'Quarter-over-quarter change', usage: 'QOQ_CHANGE({Measure})' },
    { name: 'QOQ_CHANGE_PERCENT', desc: 'Quarter-over-quarter %', usage: 'QOQ_CHANGE_PERCENT({Measure})' },
    { name: 'YOY_CHANGE', desc: 'Year-over-year change', usage: 'YOY_CHANGE({Measure})' },
    { name: 'YOY_CHANGE_PERCENT', desc: 'Year-over-year %', usage: 'YOY_CHANGE_PERCENT({Measure})' },
    { name: 'ROLLING_DAYS', desc: 'Trailing N days', usage: 'ROLLING_DAYS({Measure}, 30)' },
    { name: 'ROLLING_WEEKS', desc: 'Trailing N weeks', usage: 'ROLLING_WEEKS({Measure}, 4)' },
    { name: 'ROLLING_MONTHS', desc: 'Trailing N months', usage: 'ROLLING_MONTHS({Measure}, 3)' },
    { name: 'ROLLING_QUARTERS', desc: 'Trailing N quarters', usage: 'ROLLING_QUARTERS({Measure}, 4)' },
    { name: 'ROLLING_YEARS', desc: 'Trailing N years', usage: 'ROLLING_YEARS({Measure}, 2)' },
    { name: 'ROLLING_PERIOD', desc: 'Trailing N of a unit', usage: 'ROLLING_PERIOD({Measure}, 6, UNIT=month)' },
  ]},
  { cat: 'Aggregation', fns: [
    { name: 'SUM', desc: 'Sum of values', usage: 'SUM(field)' },
    { name: 'AVG', desc: 'Average', usage: 'AVG(field)' },
    { name: 'COUNT', desc: 'Count rows', usage: 'COUNT(field)' },
    { name: 'COUNT_DISTINCT', desc: 'Unique count', usage: 'COUNT_DISTINCT(field)' },
    { name: 'MIN', desc: 'Minimum', usage: 'MIN(field)' },
    { name: 'MAX', desc: 'Maximum', usage: 'MAX(field)' },
    { name: 'MEDIAN', desc: 'Median value', usage: 'MEDIAN(field)' },
  ]},
  { cat: 'Math', fns: [
    { name: 'ABS', desc: 'Absolute value', usage: 'ABS(field)' },
    { name: 'ROUND', desc: 'Round decimals', usage: 'ROUND(field, 2)' },
    { name: 'CLAMP', desc: 'Bound a value into [lo, hi]', usage: 'CLAMP(field, 0, 100)' },
    { name: 'SIGN', desc: 'Sign: -1 / 0 / 1', usage: 'SIGN(field)' },
    { name: 'POWER', desc: 'Raise to a power', usage: 'POWER(field, 2)' },
    { name: 'SQRT', desc: 'Square root', usage: 'SQRT(field)' },
    { name: 'MOD', desc: 'Remainder (a mod b)', usage: 'MOD(field, 12)' },
    { name: 'EXP', desc: 'e ^ x', usage: 'EXP(field)' },
    { name: 'LN', desc: 'Natural logarithm', usage: 'LN(field)' },
    { name: 'LOG', desc: 'Logarithm (base 10 default)', usage: 'LOG(field, 10)' },
    { name: 'CEIL', desc: 'Round up', usage: 'CEIL(field)' },
    { name: 'FLOOR', desc: 'Round down', usage: 'FLOOR(field)' },
    { name: 'SQRT', desc: 'Square root', usage: 'SQRT(field)' },
    { name: 'POWER', desc: 'Raise to power', usage: 'POWER(field, 2)' },
  ]},
  { cat: 'Conditional', fns: [
    { name: 'IF', desc: 'Conditional', usage: 'IF(cond, then, else)' },
    { name: 'CASE', desc: 'Multi-branch', usage: 'CASE WHEN cond THEN val END' },
    { name: 'COALESCE', desc: 'First non-null', usage: 'COALESCE(a, b)' },
    { name: 'NULLIF', desc: 'Null if equal', usage: 'NULLIF(a, 0)' },
  ]},
  { cat: 'Conditional Agg', fns: [
    { name: 'SUM_IF', desc: 'Sum where condition', usage: 'SUM_IF(field, condition)' },
    { name: 'COUNT_IF', desc: 'Count rows where', usage: 'COUNT_IF(condition)' },
    { name: 'COUNT_DISTINCT_IF', desc: 'Distinct count where', usage: 'COUNT_DISTINCT_IF(field, condition)' },
    { name: 'AVG_IF', desc: 'Average where', usage: 'AVG_IF(field, condition)' },
    { name: 'MIN_IF', desc: 'Minimum where', usage: 'MIN_IF(field, condition)' },
    { name: 'MAX_IF', desc: 'Maximum where', usage: 'MAX_IF(field, condition)' },
    { name: 'MEDIAN_IF', desc: 'Median where', usage: 'MEDIAN_IF(field, condition)' },
    { name: 'PERCENTILE_IF', desc: 'Percentile where', usage: 'PERCENTILE_IF(field, 0.9, condition)' },
    { name: 'WEIGHTED_AVG', desc: 'Weighted average', usage: 'WEIGHTED_AVG(value, weight)' },
  ]},
  { cat: 'Window', fns: [
    { name: 'LAG', desc: 'Previous row', usage: 'LAG(field, 1)' },
    { name: 'LEAD', desc: 'Next row', usage: 'LEAD(field, 1)' },
    { name: 'RANK', desc: 'Rank by value', usage: 'RANK() OVER (ORDER BY field)' },
    { name: 'DENSE_RANK', desc: 'Dense rank', usage: 'DENSE_RANK() OVER (ORDER BY field)' },
    { name: 'NTILE', desc: 'N-tile bucket', usage: 'NTILE(4) OVER (ORDER BY field)' },
    { name: 'RUNNING_SUM', desc: 'Cumulative sum', usage: 'RUNNING_SUM(field)' },
    { name: 'ROLLING_AVG', desc: 'Rolling avg', usage: 'ROLLING_AVG(field, 3)' },
  ]},
  { cat: 'Ratios', fns: [
    { name: 'SAFE_DIVIDE', desc: 'Divide, 0-safe → NULL', usage: 'SAFE_DIVIDE(numerator, denominator)' },
    { name: 'RATIO', desc: 'Ratio, 0-safe', usage: 'RATIO(numerator, denominator)' },
    { name: 'PERCENT', desc: 'Percent, 0-safe', usage: 'PERCENT(numerator, denominator)' },
    { name: 'PERCENT_CHANGE', desc: '% change vs prior', usage: 'PERCENT_CHANGE(current, prior)' },
    { name: 'ABSOLUTE_CHANGE', desc: 'Absolute change', usage: 'ABSOLUTE_CHANGE(current, prior)' },
    { name: 'BASIS_POINT_CHANGE', desc: 'Change in basis points (×10000)', usage: 'BASIS_POINT_CHANGE(current, prior)' },
    { name: 'PCT_OF_TOTAL', desc: '% of total', usage: 'field / SUM(field) * 100' },
    { name: 'PERCENT_OF_TOTAL', desc: 'Share of grand total', usage: 'PERCENT_OF_TOTAL({Measure})' },
    { name: 'PERCENT_OF_PARENT', desc: 'Share within a parent dim', usage: 'PERCENT_OF_PARENT({Measure}, [Region])' },
    { name: 'RANK_BY', desc: 'Rank position (1 = largest)', usage: 'RANK_BY({Measure})' },
    { name: 'DENSE_RANK_BY', desc: 'Dense rank (ties share, no gaps)', usage: 'DENSE_RANK_BY({Measure})' },
    { name: 'PERCENT_RANK_BY', desc: 'Percentile rank (0–100)', usage: 'PERCENT_RANK_BY({Measure})' },
    { name: 'QUARTILE_BY', desc: 'Quartile band (1–4)', usage: 'QUARTILE_BY({Measure})' },
    { name: 'DECILE_BY', desc: 'Decile band (1–10)', usage: 'DECILE_BY({Measure})' },
    { name: 'NTILE_BY', desc: 'N equal bands', usage: 'NTILE_BY({Measure}, 5)' },
    { name: 'TOP_N', desc: '1 if in the top N by measure, else 0', usage: 'TOP_N({Measure}, 10)' },
    { name: 'BOTTOM_N', desc: '1 if in the bottom N by measure, else 0', usage: 'BOTTOM_N({Measure}, 10)' },
    { name: 'CUMULATIVE_PERCENT', desc: 'Pareto running cumulative %', usage: 'CUMULATIVE_PERCENT({Measure})' },
    { name: 'RUNNING_SUM', desc: 'Cumulative running total', usage: 'RUNNING_SUM({Measure})' },
    { name: 'RUNNING_MAX', desc: 'Running maximum', usage: 'RUNNING_MAX({Measure})' },
    { name: 'RUNNING_MIN', desc: 'Running minimum', usage: 'RUNNING_MIN({Measure})' },
    { name: 'MOVING_AVG', desc: 'Trailing N-row moving average', usage: 'MOVING_AVG({Measure}, 3)' },
    { name: 'MOVING_SUM', desc: 'Trailing N-row moving sum', usage: 'MOVING_SUM({Measure}, 3)' },
    { name: 'RUNNING_AVG', desc: 'Cumulative running average', usage: 'RUNNING_AVG({Measure})' },
    { name: 'RUNNING_COUNT', desc: 'Cumulative running count', usage: 'RUNNING_COUNT({Measure})' },
    { name: 'MOVING_MEDIAN', desc: 'Trailing N-row moving median', usage: 'MOVING_MEDIAN({Measure}, 3)' },
    { name: 'COMPOUND_GROWTH_RATE', desc: 'Cumulative compound growth % from the first period', usage: 'COMPOUND_GROWTH_RATE({Measure})' },
    { name: 'CAGR', desc: 'Compound annual growth rate % (per period)', usage: 'CAGR({Measure})' },
    { name: 'PARETO_CLASS', desc: 'A/B/C Pareto class', usage: 'PARETO_CLASS({Measure})' },
    { name: 'GROWTH_RATE', desc: 'Growth %', usage: '(field - PRIOR_PERIOD(field, 1)) / ABS(PRIOR_PERIOD(field, 1)) * 100' },
    { name: 'NORMALIZE', desc: 'Normalize', usage: '(field - MIN(field)) / (MAX(field) - MIN(field))' },
  ]},
  { cat: 'Targets & Thresholds', fns: [
    { name: 'VARIANCE_TO_TARGET', desc: 'Actual − target (positive = above target)', usage: 'VARIANCE_TO_TARGET({Actual}, {Target})' },
    { name: 'VARIANCE_TO_BUDGET', desc: 'Actual − budget (positive = over budget)', usage: 'VARIANCE_TO_BUDGET({Actual}, {Budget})' },
    { name: 'PERCENT_TO_TARGET', desc: '100 × actual / target (0-safe → NULL)', usage: 'PERCENT_TO_TARGET({Actual}, {Target})' },
    { name: 'ABOVE_THRESHOLD', desc: '1 if value > threshold else 0 (3rd arg = inclusive)', usage: 'ABOVE_THRESHOLD({Measure}, 100)' },
    { name: 'BELOW_THRESHOLD', desc: '1 if value < threshold else 0 (3rd arg = inclusive)', usage: 'BELOW_THRESHOLD({Measure}, 100)' },
    { name: 'WITHIN_RANGE', desc: '1 if lo ≤ value ≤ hi else 0', usage: 'WITHIN_RANGE({Measure}, 80, 120)' },
    { name: 'STATUS', desc: 'Band label by breakpoints; trailing label = fallback', usage: 'STATUS({Measure}, 80, "Low", 95, "OK", "High")' },
    { name: 'BUCKET', desc: 'Numeric binning with auto range labels', usage: 'BUCKET({Measure}, 100, 500, 1000)' },
    { name: 'IN_GROUP', desc: 'Membership in a governed value-set group (use in a condition)', usage: 'SUM_IF(premium, IN_GROUP(state, "HighRiskStates"))' },
    { name: 'MAP_GROUP', desc: 'Map a value to its governed group label', usage: 'MAP_GROUP(state, "Region")' },
  ]},
  { cat: 'As-Of / Effective-Dated', fns: [
    { name: 'COUNT_ACTIVE_AS_OF', desc: 'Distinct entities active at a date (SCD2, end-exclusive)', usage: 'COUNT_ACTIVE_AS_OF(policy_number, effective_start, effective_end, SELECTED_DATE())' },
    { name: 'ACTIVE_AS_OF', desc: 'Count of records active at a date', usage: 'ACTIVE_AS_OF(effective_start, effective_end, SELECTED_DATE())' },
    { name: 'BALANCE_AS_OF', desc: 'Running balance transacted on-or-before a date', usage: 'BALANCE_AS_OF(amount, transaction_date, SELECTED_DATE())' },
    { name: 'FIRST_DATE', desc: 'Earliest value of a date field', usage: 'FIRST_DATE(effective_start)' },
    { name: 'LAST_DATE', desc: 'Latest value of a date field', usage: 'LAST_DATE(effective_start)' },
    { name: 'DATEDIFF', desc: 'Signed difference between two dates (days/months/years)', usage: 'DATEDIFF(LAST_DATE(d), FIRST_DATE(d), "days")' },
    { name: 'LATEST_VALUE', desc: 'Value from the latest-dated row', usage: 'LATEST_VALUE(premium, effective_start)' },
    { name: 'PREVIOUS_VALUE', desc: 'Value from the second-latest-dated row', usage: 'PREVIOUS_VALUE(premium, effective_start)' },
    { name: 'CHANGED_FROM_TO', desc: '1 if the field changed from→to over time', usage: 'CHANGED_FROM_TO(status, effective_date, "Open", "Closed")' },
    { name: 'DAYS_IN_STATUS', desc: 'Days since the latest change on-or-before a date', usage: 'DAYS_IN_STATUS(status, effective_date, "2024-12-31")' },
    { name: 'VALUE_AS_OF', desc: 'Value as it stood on a chosen date', usage: 'VALUE_AS_OF(premium, effective_date, SELECTED_DATE())' },
    { name: 'LATEST_AS_OF', desc: 'Latest value on-or-before a date', usage: 'LATEST_AS_OF(premium, effective_date, SELECTED_DATE())' },
    { name: 'STATUS_AS_OF', desc: 'Status from the latest row on-or-before a date', usage: 'STATUS_AS_OF(status, effective_date, SELECTED_DATE())' },
    { name: 'SELECTED_DATE', desc: 'The dashboard/query as-of date (late-bound)', usage: 'SELECTED_DATE()' },
  ]},
  { cat: 'Filter Context', fns: [
    { name: 'REMOVE_FILTER', desc: 'Recompute ignoring all filters, or only named ones', usage: 'REMOVE_FILTER(SUM, premium, line_of_business)' },
    { name: 'IGNORE_FILTER', desc: 'Alias of REMOVE_FILTER', usage: 'IGNORE_FILTER(SUM, premium)' },
  ]},
  { cat: 'Relationships', fns: [
    { name: 'RELATED', desc: 'Single attribute from a parent (many-to-one) entity', usage: 'RELATED(line_of_business)' },
    { name: 'LOOKUP', desc: 'Look up a value on a related entity (optional key hint)', usage: 'LOOKUP(line_of_business, policy_number)' },
    { name: 'SUM_RELATED', desc: 'Sum a measure on a related (child) entity', usage: 'SUM_RELATED(paid_loss)' },
    { name: 'COUNT_RELATED', desc: 'Distinct count of related (child) rows', usage: 'COUNT_RELATED(claim_id)' },
    { name: 'EXISTS_RELATED', desc: '1 when any related row exists, else 0', usage: 'EXISTS_RELATED(claim_id)' },
    { name: 'LATEST_RELATED', desc: 'Value from the latest-dated related row', usage: 'LATEST_RELATED(paid_loss, loss_date)' },
  ]},
  { cat: 'Governed', fns: [
    { name: 'CERTIFIED_MEASURE', desc: 'Value of a measure, only if it is certified', usage: 'CERTIFIED_MEASURE("Written Premium")' },
    { name: 'MEASURE_STATUS', desc: 'Certification status of a named measure', usage: 'MEASURE_STATUS("Written Premium")' },
    { name: 'MEASURE_VERSION', desc: 'Certified version number of a named measure', usage: 'MEASURE_VERSION("Written Premium")' },
  ]},
  { cat: 'Cohort & Lifecycle', fns: [
    { name: 'NEW_ENTITIES', desc: 'Entities whose first-ever event is in the period', usage: 'NEW_ENTITIES(policy_number, effective_start, UNIT=month)' },
    { name: 'RETURNING_ENTITIES', desc: 'Active this period and the period before', usage: 'RETURNING_ENTITIES(policy_number, effective_start, UNIT=month)' },
    { name: 'REACTIVATED_ENTITIES', desc: 'Active again after being inactive last period', usage: 'REACTIVATED_ENTITIES(policy_number, effective_start, UNIT=month)' },
    { name: 'CHURNED_ENTITIES', desc: 'Active last period but not this one', usage: 'CHURNED_ENTITIES(policy_number, effective_start, UNIT=month)' },
    { name: 'ACTIVE_ENTITIES', desc: 'Distinct entities active in the period', usage: 'ACTIVE_ENTITIES(policy_number, effective_start, UNIT=month)' },
    { name: 'COHORT', desc: 'Entities entering their first-event cohort this period', usage: 'COHORT(policy_number, effective_start, UNIT=month)' },
    { name: 'COHORT_RETENTION', desc: 'Retention curve by cohort × periods-since', usage: 'COHORT_RETENTION(policy_number, effective_start, UNIT=month)' },
  ]},
  { cat: 'Advanced Statistics', fns: [
    { name: 'Z_SCORE_BY', desc: 'Standardised score of each row vs the series', usage: 'Z_SCORE_BY({Premium})' },
    { name: 'STANDARDIZE_BY', desc: 'Standardise a series (mean 0, std 1)', usage: 'STANDARDIZE_BY({Premium})' },
    { name: 'NORMALIZE_BY', desc: 'Min–max normalise a series to 0–1', usage: 'NORMALIZE_BY({Premium})' },
    { name: 'IS_OUTLIER', desc: 'Flag outliers (iqr / zscore / modzscore)', usage: 'IS_OUTLIER({Premium}, METHOD=iqr)' },
    { name: 'OUTLIER_COUNT', desc: 'Count of outliers in the series', usage: 'OUTLIER_COUNT({Premium}, METHOD=iqr)' },
    { name: 'OUTLIER_THRESHOLD', desc: 'The upper/lower outlier fence value', usage: 'OUTLIER_THRESHOLD({Premium}, BOUND=upper)' },
    { name: 'INTERQUARTILE_RANGE', desc: 'Q3 − Q1 spread of the series', usage: 'INTERQUARTILE_RANGE({Premium})' },
    { name: 'COEFFICIENT_OF_VARIATION', desc: 'Std ÷ mean of the series', usage: 'COEFFICIENT_OF_VARIATION({Premium})' },
    { name: 'CORRELATION_BY', desc: 'Pearson correlation of two measures', usage: 'CORRELATION_BY({Premium}, {Loss})' },
  ]},
  { cat: 'Trend', fns: [
    { name: 'TREND', desc: 'Fitted trend-line value per period', usage: 'TREND({Premium})' },
    { name: 'TREND_SLOPE', desc: 'Slope of the fitted trend line', usage: 'TREND_SLOPE({Premium})' },
    { name: 'TREND_DIRECTION', desc: 'up / down / flat', usage: 'TREND_DIRECTION({Premium})' },
    { name: 'TREND_STRENGTH', desc: 'Fit strength (R², 0–1)', usage: 'TREND_STRENGTH({Premium})' },
  ]},
  { cat: 'Forecast & Anomaly', fns: [
    { name: 'FORECAST', desc: 'Project future periods (auto-selected model)', usage: 'FORECAST({Premium}, PERIODS=6)' },
    { name: 'FORECAST_LOWER_BOUND', desc: 'Lower confidence bound of the forecast', usage: 'FORECAST_LOWER_BOUND({Premium}, PERIODS=6)' },
    { name: 'FORECAST_UPPER_BOUND', desc: 'Upper confidence bound of the forecast', usage: 'FORECAST_UPPER_BOUND({Premium}, PERIODS=6)' },
    { name: 'EXPECTED_VALUE', desc: 'Model expected value (observed + forecast)', usage: 'EXPECTED_VALUE({Premium})' },
    { name: 'DEVIATION_FROM_EXPECTED', desc: 'Actual − trend-expected per period', usage: 'DEVIATION_FROM_EXPECTED({Premium})' },
    { name: 'ANOMALY_SCORE', desc: 'Standardised residual from the trend', usage: 'ANOMALY_SCORE({Premium})' },
    { name: 'IS_ANOMALY', desc: 'Flag anomalous periods', usage: 'IS_ANOMALY({Premium}, SENSITIVITY=3)' },
  ]},
  { cat: 'Data Quality', fns: [
    { name: 'NULL_COUNT', desc: 'Count of missing (NULL) values', usage: 'NULL_COUNT(field)' },
    { name: 'NULL_PERCENT', desc: '% of rows where the field is missing', usage: 'NULL_PERCENT(field)' },
    { name: 'COMPLETENESS_SCORE', desc: '% of rows where the field is populated', usage: 'COMPLETENESS_SCORE(field)' },
    { name: 'DISTINCT_PERCENT', desc: '% of distinct values over total rows', usage: 'DISTINCT_PERCENT(field)' },
    { name: 'DUPLICATE_COUNT', desc: 'Non-null count minus distinct count', usage: 'DUPLICATE_COUNT(field)' },
    { name: 'VALID_PERCENT', desc: '% of rows in a permitted value set', usage: 'VALID_PERCENT(field, "A", "B")' },
    { name: 'VALIDITY_SCORE', desc: '% of populated rows that are valid', usage: 'VALIDITY_SCORE(field, "A", "B")' },
    { name: 'DATA_QUALITY_SCORE', desc: 'Composite score: completeness, or mean of completeness + validity', usage: 'DATA_QUALITY_SCORE(field, "A", "B")' },
  ]},

  { cat: 'Date', fns: [
    { name: 'DATEDIFF', desc: 'Days between', usage: 'DATEDIFF(date1, date2, "days")' },
    { name: 'MONTHS_BETWEEN', desc: 'Months between', usage: 'MONTHS_BETWEEN(date1, date2)' },
    { name: 'YEAR', desc: 'Extract year', usage: 'YEAR(date_field)' },
    { name: 'QUARTER', desc: 'Extract quarter (1-4)', usage: 'QUARTER(date_field)' },
    { name: 'MONTH', desc: 'Extract month (1-12)', usage: 'MONTH(date_field)' },
    { name: 'DAY', desc: 'Extract day of month', usage: 'DAY(date_field)' },
    { name: 'FISCAL_YEAR', desc: 'Fiscal year', usage: 'FISCAL_YEAR(date_field, 7)' },
  ]},
  { cat: 'Insurance', fns: [
    { name: 'POLICY_YEAR', desc: 'Policy year', usage: 'POLICY_YEAR(effective_date, eval_date)' },
    { name: 'EARNED_FACTOR', desc: 'Earned factor', usage: 'EARNED_FACTOR(eff_date, exp_date, eval_date)' },
    { name: 'LOSS_RATIO', desc: 'Loss ratio', usage: 'incurred_losses / earned_premium' },
    { name: 'COMBINED_RATIO', desc: 'Combined ratio', usage: 'loss_ratio + expense_ratio' },
    { name: 'LINK_RATIO', desc: 'Dev factor', usage: 'CUM_PAID_N / CUM_PAID_N_MINUS_1' },
    { name: 'IBNR_FACTOR', desc: 'IBNR reserve', usage: '1 - (1 / CUM_DEVELOPMENT_FACTOR)' },
    { name: 'ULTIMATE_LOSS', desc: 'Chain ladder ultimate', usage: 'latest_cumulative * CDF_TO_ULTIMATE' },
    { name: 'BF_ULTIMATE', desc: 'Bornhuetter-Ferguson', usage: 'actual + (earned_premium * ELR * pct_unreported)' },
    { name: 'CAPE_COD_ELR', desc: 'Cape Cod ELR', usage: 'SUM(actual) / SUM(earned_premium * pct_reported)' },
  ]},
  { cat: 'LOD', fns: [
    { name: 'FIXED', desc: 'Fixed LOD', usage: '{ FIXED [dim] : SUM(measure) }' },
    { name: 'INCLUDE', desc: 'Include LOD', usage: '{ INCLUDE [dim] : AVG(measure) }' },
    { name: 'EXCLUDE', desc: 'Exclude LOD', usage: '{ EXCLUDE [dim] : SUM(measure) }' },
  ]},
  { cat: 'Stats', fns: [
    { name: 'STDDEV', desc: 'Std deviation', usage: 'STDDEV(field)' },
    { name: 'VARIANCE', desc: 'Variance', usage: 'VARIANCE(field)' },
    { name: 'ZSCORE', desc: 'Z-score', usage: '(field - AVG(field)) / STDDEV(field)' },
  ]},
  { cat: 'Triangle', fns: [
    { name: 'LINK_RATIO', desc: 'Age-to-age factor', usage: 'value_at_N / value_at_N_minus_1' },
    { name: 'CDF', desc: 'Cumulative dev factor', usage: 'PRODUCT(remaining_link_ratios)' },
    { name: 'PCT_REPORTED', desc: '% reported', usage: '1 / CDF_TO_ULTIMATE' },
    { name: 'PCT_UNREPORTED', desc: '% unreported', usage: '1 - (1 / CDF_TO_ULTIMATE)' },
    { name: 'ULTIMATE_CL', desc: 'Chain ladder ultimate', usage: 'latest_diagonal * CDF_TO_ULTIMATE' },
    { name: 'ULTIMATE_BF', desc: 'BF ultimate', usage: 'actual + (earned_prem * ELR * pct_unreported)' },
    { name: 'ULTIMATE_CC', desc: 'Cape Cod ultimate', usage: 'actual + (earned_prem * cape_cod_ELR * pct_unreported)' },
    { name: 'IBNR', desc: 'IBNR reserve', usage: 'ultimate - latest_diagonal' },
    { name: 'TAIL_FACTOR', desc: 'Tail factor', usage: 'factor_beyond_last_dev_period' },
  ]},
  { cat: 'Period', fns: [
    { name: 'PRIOR_PERIOD', desc: 'Prior period value', usage: 'PRIOR_PERIOD(field, 1)' },
    { name: 'PERIOD_VARIANCE', desc: 'Period variance', usage: 'current_value - prior_value' },
    { name: 'PERIOD_VAR_PCT', desc: 'Variance %', usage: '(current - prior) / ABS(prior) * 100' },
    { name: 'LATEST', desc: 'Latest value (semi-additive)', usage: 'LATEST(field)' },
    { name: 'FIRST', desc: 'First value (semi-additive)', usage: 'FIRST(field)' },
  ]},
];

const ALL_FUNCTIONS = FORMULA_CATEGORIES.flatMap(c => c.fns);
const KEYWORDS = ['CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'AND', 'OR', 'NOT', 'OVER', 'ORDER', 'BY', 'FIXED', 'INCLUDE', 'EXCLUDE', 'NULL', 'TRUE', 'FALSE', 'AS'];

// Live plain-English hint: describe the recognised function families in a
// formula as the author types. Heuristic + client-side (instant, no network);
// the backend /measures/test returns the authoritative explanation on Check.
const _ROLL_UNIT = { ROLLING_DAYS: 'day', ROLLING_WEEKS: 'week', ROLLING_MONTHS: 'month', ROLLING_QUARTERS: 'quarter', ROLLING_YEARS: 'year' };
const _TODATE = { WTD: 'week-to-date', MTD: 'month-to-date', QTD: 'quarter-to-date', YTD: 'year-to-date', ITD: 'inception-to-date', LTD: 'life-to-date' };
export function describeFormula(text) {
  if (!text || !text.trim()) return '';
  const t = text;
  const seen = new Set();
  const parts = [];
  const add = (p) => { if (p && !seen.has(p)) { seen.add(p); parts.push(p); } };
  const arg1 = (fn) => { const m = new RegExp(fn + '\\s*\\(\\s*\\{?\\s*([^,(){}]+?)\\s*\\}?\\s*[,)]', 'i').exec(t); return m ? m[1].trim() : 'the measure'; };
  // Conditional aggregation
  [['SUM_IF', 'sum'], ['COUNT_DISTINCT_IF', 'distinct count'], ['COUNT_IF', 'count'], ['AVG_IF', 'average'], ['MIN_IF', 'minimum'], ['MAX_IF', 'maximum'], ['MEDIAN_IF', 'median'], ['PERCENTILE_IF', 'percentile']].forEach(([fn, word]) => {
    if (new RegExp('\\b' + fn + '\\s*\\(', 'i').test(t)) add(`${word} of ${fn === 'COUNT_IF' ? 'rows' : arg1(fn)} that match a condition`);
  });
  if (/\bWEIGHTED_AVG\s*\(/i.test(t)) add('a weighted average');
  // Rolling
  Object.keys(_ROLL_UNIT).forEach(fn => { if (new RegExp('\\b' + fn + '\\s*\\(', 'i').test(t)) add(`a trailing ${_ROLL_UNIT[fn]} total of ${arg1(fn)}`); });
  if (/\bROLLING_PERIOD\s*\(/i.test(t)) add(`a trailing total of ${arg1('ROLLING_PERIOD')}`);
  // To-date + comparisons
  Object.keys(_TODATE).forEach(fn => { if (new RegExp('\\b' + fn + '\\s*\\(', 'i').test(t)) add(`${_TODATE[fn]} ${arg1(fn)}`); });
  [['YOY_CHANGE_PERCENT', 'year-over-year % change'], ['QOQ_CHANGE_PERCENT', 'quarter-over-quarter % change'], ['MOM_CHANGE_PERCENT', 'month-over-month % change'], ['YOY_CHANGE', 'year-over-year change'], ['QOQ_CHANGE', 'quarter-over-quarter change'], ['MOM_CHANGE', 'month-over-month change'], ['PRIOR_YEAR', "the prior year's value"], ['PRIOR_QUARTER', "the prior quarter's value"], ['PRIOR_MONTH', "the prior month's value"]].forEach(([fn, word]) => {
    if (new RegExp('\\b' + fn + '\\s*\\(', 'i').test(t)) add(`${word} of ${arg1(fn)}`);
  });
  // Share
  if (/\bPERCENT_OF_TOTAL\s*\(/i.test(t)) add(`${arg1('PERCENT_OF_TOTAL')} as a share of the grand total`);
  if (/\bPERCENT_OF_PARENT\s*\(/i.test(t)) add(`${arg1('PERCENT_OF_PARENT')} as a share of its parent group`);
  if (/\bRANK_BY\s*\(/i.test(t)) add(`the rank of ${arg1('RANK_BY')} (largest = 1)`);
  if (/\bDENSE_RANK_BY\s*\(/i.test(t)) add(`the dense rank of ${arg1('DENSE_RANK_BY')} (ties share a rank)`);
  if (/\bPERCENT_RANK_BY\s*\(/i.test(t)) add(`the percentile rank of ${arg1('PERCENT_RANK_BY')} (0–100)`);
  if (/\bQUARTILE_BY\s*\(/i.test(t)) add(`the quartile band (1–4) of ${arg1('QUARTILE_BY')}`);
  if (/\bDECILE_BY\s*\(/i.test(t)) add(`the decile band (1–10) of ${arg1('DECILE_BY')}`);
  if (/\bNTILE_BY\s*\(/i.test(t)) add(`an N-tile band of ${arg1('NTILE_BY')}`);
  if (/\bTOP_N\s*\(/i.test(t)) add(`whether ${arg1('TOP_N')} is among the top N`);
  if (/\bBOTTOM_N\s*\(/i.test(t)) add(`whether ${arg1('BOTTOM_N')} is among the bottom N`);
  if (/\bCUMULATIVE_PERCENT\s*\(/i.test(t)) add(`the running cumulative % of ${arg1('CUMULATIVE_PERCENT')} (Pareto)`);
  if (/\bRUNNING_SUM\s*\(/i.test(t)) add(`a running (cumulative) total of ${arg1('RUNNING_SUM')}`);
  if (/\bRUNNING_MAX\s*\(/i.test(t)) add(`the running maximum of ${arg1('RUNNING_MAX')}`);
  if (/\bRUNNING_MIN\s*\(/i.test(t)) add(`the running minimum of ${arg1('RUNNING_MIN')}`);
  if (/\bMOVING_AVG\s*\(/i.test(t)) add(`a trailing moving average of ${arg1('MOVING_AVG')}`);
  if (/\bMOVING_SUM\s*\(/i.test(t)) add(`a trailing moving sum of ${arg1('MOVING_SUM')}`);
  if (/\bMOVING_MEDIAN\s*\(/i.test(t)) add(`a trailing moving median of ${arg1('MOVING_MEDIAN')}`);
  if (/\bRUNNING_AVG\s*\(/i.test(t)) add(`a running (cumulative) average of ${arg1('RUNNING_AVG')}`);
  if (/\bRUNNING_COUNT\s*\(/i.test(t)) add(`a running (cumulative) count of ${arg1('RUNNING_COUNT')}`);
  if (/\bCOMPOUND_GROWTH_RATE\s*\(/i.test(t)) add(`the cumulative compound growth of ${arg1('COMPOUND_GROWTH_RATE')} since the first period`);
  if (/\bCAGR\s*\(/i.test(t)) add(`the compound annual growth rate (CAGR) of ${arg1('CAGR')}`);
  if (/\bPARETO_CLASS\s*\(/i.test(t)) add(`an A/B/C Pareto class for ${arg1('PARETO_CLASS')}`);
  // Safe ratios / date convenience
  if (/\bSAFE_DIVIDE\s*\(|\bRATIO\s*\(/i.test(t)) add('a divide-by-zero-safe ratio');
  if (/\bBASIS_POINT_CHANGE\s*\(/i.test(t)) add('the change in basis points vs the prior value');
  if (/\bDAYS_BETWEEN\s*\(/i.test(t)) add('the number of days between two dates');
  if (/\bMONTHS_BETWEEN\s*\(/i.test(t)) add('the number of months between two dates');
  if (/\bADD_BUSINESS_DAYS\s*\(/i.test(t)) add('a date shifted by business days');
  if (/\bAGE_IN_DAYS\s*\(/i.test(t)) add('the age in days from a date to today');
  if (/\bDATE_BUCKET\s*\(/i.test(t)) add('a date truncated to a period bucket');
  // Targets & thresholds (§21)
  if (/\bVARIANCE_TO_TARGET\s*\(/i.test(t)) add(`the variance of ${arg1('VARIANCE_TO_TARGET')} to its target`);
  if (/\bVARIANCE_TO_BUDGET\s*\(/i.test(t)) add(`the variance of ${arg1('VARIANCE_TO_BUDGET')} to its budget`);
  if (/\bPERCENT_TO_TARGET\s*\(/i.test(t)) add(`${arg1('PERCENT_TO_TARGET')} as a percent of its target`);
  if (/\bABOVE_THRESHOLD\s*\(/i.test(t)) add(`whether ${arg1('ABOVE_THRESHOLD')} is above a threshold`);
  if (/\bBELOW_THRESHOLD\s*\(/i.test(t)) add(`whether ${arg1('BELOW_THRESHOLD')} is below a threshold`);
  if (/\bWITHIN_RANGE\s*\(/i.test(t)) add(`whether ${arg1('WITHIN_RANGE')} falls within a range`);
  if (/\bSTATUS\s*\(/i.test(t)) add(`a status band label for ${arg1('STATUS')}`);
  if (/\bBUCKET\s*\(/i.test(t)) add(`a range bucket label for ${arg1('BUCKET')}`);
  if (/\bIN_GROUP\s*\(/i.test(t)) add(`whether ${arg1('IN_GROUP')} is in a governed value-set group`);
  if (/\bMAP_GROUP\s*\(/i.test(t)) add(`${arg1('MAP_GROUP')} mapped to its governed group label`);
  // As-of / effective-dated (§15)
  if (/\bCOUNT_ACTIVE_AS_OF\s*\(/i.test(t)) add(`the count of distinct ${arg1('COUNT_ACTIVE_AS_OF')} active as of a date`);
  if (/\bACTIVE_AS_OF\s*\(/i.test(t)) add('the count of records active as of a date');
  if (/\bBALANCE_AS_OF\s*\(/i.test(t)) add(`a running balance of ${arg1('BALANCE_AS_OF')} on-or-before a date`);
  if (/\bFIRST_DATE\s*\(/i.test(t)) add(`the earliest ${arg1('FIRST_DATE')}`);
  if (/\bLAST_DATE\s*\(/i.test(t)) add(`the latest ${arg1('LAST_DATE')}`);
  if (/\bDATEDIFF\s*\(/i.test(t)) add('the difference between two dates');
  if (/\bLATEST_VALUE\s*\(/i.test(t)) add(`${arg1('LATEST_VALUE')} from the latest-dated row`);
  if (/\bPREVIOUS_VALUE\s*\(/i.test(t)) add(`${arg1('PREVIOUS_VALUE')} from the second-latest-dated row`);
  if (/\bCHANGED_FROM_TO\s*\(/i.test(t)) add(`whether ${arg1('CHANGED_FROM_TO')} changed from one value to another`);
  if (/\bDAYS_IN_STATUS\s*\(/i.test(t)) add('the number of days in the current status');
  if (/\bVALUE_AS_OF\s*\(/i.test(t)) add(`${arg1('VALUE_AS_OF')} as it stood on a chosen date`);
  if (/\bLATEST_AS_OF\s*\(/i.test(t)) add(`${arg1('LATEST_AS_OF')} from the latest row on-or-before a date`);
  if (/\bSTATUS_AS_OF\s*\(/i.test(t)) add(`the status of ${arg1('STATUS_AS_OF')} on-or-before a date`);
  // Filter context (§9)
  if (/\bREMOVE_FILTER\s*\(/i.test(t) || /\bIGNORE_FILTER\s*\(/i.test(t)) add('a value that ignores one or more query filters');
  // Relationships (§16)
  if (/\bSUM_RELATED\s*\(/i.test(t)) add(`the sum of ${arg1('SUM_RELATED')} on a related entity`);
  if (/\bCOUNT_RELATED\s*\(/i.test(t)) add(`the distinct count of related ${arg1('COUNT_RELATED')} rows`);
  if (/\bEXISTS_RELATED\s*\(/i.test(t)) add(`whether any related ${arg1('EXISTS_RELATED')} row exists`);
  if (/\bLATEST_RELATED\s*\(/i.test(t)) add(`${arg1('LATEST_RELATED')} from the latest-dated related row`);
  if (/\bRELATED\s*\(/i.test(t)) add(`${arg1('RELATED')} from a related parent entity`);
  if (/\bLOOKUP\s*\(/i.test(t)) add(`${arg1('LOOKUP')} looked up on a related entity`);
  // Governed (§22)
  if (/\bCERTIFIED_MEASURE\s*\(/i.test(t)) add('the certified version of a governed measure');
  if (/\bMEASURE_STATUS\s*\(/i.test(t)) add('the certification status of a named measure');
  if (/\bMEASURE_VERSION\s*\(/i.test(t)) add('the certified version number of a named measure');
  // Cohort & lifecycle (§17)
  if (/\bNEW_ENTITIES\s*\(/i.test(t)) add(`the count of ${arg1('NEW_ENTITIES')} entering (first event) each period`);
  if (/\bRETURNING_ENTITIES\s*\(/i.test(t)) add('the count of entities active this period and the one before');
  if (/\bREACTIVATED_ENTITIES\s*\(/i.test(t)) add('the count of entities active again after being inactive');
  if (/\bCHURNED_ENTITIES\s*\(/i.test(t)) add('the count of entities that went inactive this period');
  if (/\bACTIVE_ENTITIES\s*\(/i.test(t)) add('the count of distinct entities active each period');
  if (/\bCOHORT_RETENTION\s*\(/i.test(t)) add('a retention curve by cohort and periods-since');
  else if (/\bCOHORT\s*\(/i.test(t)) add('entities grouped into their first-event cohort');
  // Advanced statistics (§12)
  if (/\bZ_SCORE_BY\s*\(/i.test(t) || /\bSTANDARDIZE_BY\s*\(/i.test(t)) add(`the standardised score of ${arg1('Z_SCORE_BY') || arg1('STANDARDIZE_BY')} within the series`);
  if (/\bNORMALIZE_BY\s*\(/i.test(t)) add('the value min–max normalised to 0–1');
  if (/\bIS_OUTLIER\s*\(/i.test(t)) add('whether each row is a statistical outlier');
  if (/\bOUTLIER_COUNT\s*\(/i.test(t)) add('the number of outliers in the series');
  if (/\bOUTLIER_THRESHOLD\s*\(/i.test(t)) add('the outlier fence value for the series');
  if (/\bINTERQUARTILE_RANGE\s*\(/i.test(t)) add('the interquartile range (Q3 − Q1)');
  if (/\bCOEFFICIENT_OF_VARIATION\s*\(/i.test(t)) add('the coefficient of variation (std ÷ mean)');
  if (/\bCORRELATION_BY\s*\(/i.test(t)) add('the correlation between two measures');
  // Trend (§13)
  if (/\bTREND_SLOPE\s*\(/i.test(t)) add('the slope of the fitted trend line');
  else if (/\bTREND_DIRECTION\s*\(/i.test(t)) add('whether the series trends up, down or flat');
  else if (/\bTREND_STRENGTH\s*\(/i.test(t)) add('how well a trend line fits the series (R²)');
  else if (/\bTREND\s*\(/i.test(t)) add('the fitted trend value per period');
  // Forecast & anomaly (§14)
  if (/\bFORECAST_LOWER_BOUND\s*\(/i.test(t)) add('the lower confidence bound of the forecast');
  else if (/\bFORECAST_UPPER_BOUND\s*\(/i.test(t)) add('the upper confidence bound of the forecast');
  else if (/\bFORECAST\s*\(/i.test(t)) add('projected future periods for the measure');
  if (/\bEXPECTED_VALUE\s*\(/i.test(t)) add('the model-expected value for each period');
  if (/\bDEVIATION_FROM_EXPECTED\s*\(/i.test(t)) add('actual minus the trend-expected value');
  if (/\bANOMALY_SCORE\s*\(/i.test(t)) add('the standardised residual from the trend');
  if (/\bIS_ANOMALY\s*\(/i.test(t)) add('whether each period is anomalous');
  // Data quality (§20)
  if (/\bNULL_COUNT\s*\(/i.test(t)) add(`the count of missing ${arg1('NULL_COUNT')} values`);
  if (/\bNULL_PERCENT\s*\(/i.test(t)) add(`the percent of rows where ${arg1('NULL_PERCENT')} is missing`);
  if (/\bCOMPLETENESS_SCORE\s*\(/i.test(t)) add(`the percent of rows where ${arg1('COMPLETENESS_SCORE')} is populated`);
  if (/\bDISTINCT_PERCENT\s*\(/i.test(t)) add(`the percent of distinct ${arg1('DISTINCT_PERCENT')} values`);
  if (/\bDUPLICATE_COUNT\s*\(/i.test(t)) add(`the number of duplicate ${arg1('DUPLICATE_COUNT')} values`);
  if (/\bVALID_PERCENT\s*\(/i.test(t)) add(`the percent of ${arg1('VALID_PERCENT')} values in the permitted set`);
  if (/\bVALIDITY_SCORE\s*\(/i.test(t)) add(`the percent of populated ${arg1('VALIDITY_SCORE')} values that are valid`);
  if (/\bDATA_QUALITY_SCORE\s*\(/i.test(t)) add(`a composite data-quality score for ${arg1('DATA_QUALITY_SCORE')}`);

  if (!parts.length) {
    if (/[+\-*/]/.test(t)) return 'Computes an arithmetic expression over the referenced fields.';
    return '';
  }
  return 'Computes ' + parts.join('; ') + (parts.length > 1 ? ', then combines them by the formula.' : '.');
}

// Syntax highlighting: tokenize formula and return colored spans
function highlightFormula(text, fieldNames) {
  if (!text) return null;
  const fnNames = new Set(ALL_FUNCTIONS.map(f => f.name));
  const fieldSet = new Set((fieldNames || []).map(f => f.toLowerCase()));
  const kwSet = new Set(KEYWORDS);
  // Simple tokenizer
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    // Measure reference {Name}
    if (text[i] === '{') {
      let j = i + 1;
      while (j < text.length && text[j] !== '}') j++;
      const end = j < text.length ? j + 1 : j;
      tokens.push({ type: 'reference', value: text.substring(i, end) }); i = end; continue;
    }
    // String literal
    if (text[i] === '"' || text[i] === "'") {
      const q = text[i]; let j = i + 1;
      while (j < text.length && text[j] !== q) j++;
      tokens.push({ type: 'string', value: text.substring(i, j + 1) }); i = j + 1; continue;
    }
    // Number
    if (/\d/.test(text[i])) {
      let j = i; while (j < text.length && /[\d.]/.test(text[j])) j++;
      tokens.push({ type: 'number', value: text.substring(i, j) }); i = j; continue;
    }
    // Word (identifier / keyword / function)
    if (/[a-zA-Z_]/.test(text[i])) {
      let j = i; while (j < text.length && /[a-zA-Z0-9_]/.test(text[j])) j++;
      const word = text.substring(i, j);
      const upper = word.toUpperCase();
      if (fnNames.has(upper)) tokens.push({ type: 'function', value: word });
      else if (kwSet.has(upper)) tokens.push({ type: 'keyword', value: word });
      else if (fieldSet.has(word.toLowerCase())) tokens.push({ type: 'field', value: word });
      else tokens.push({ type: 'identifier', value: word });
      i = j; continue;
    }
    // Operator
    if ('+-*/<>=!'.includes(text[i])) {
      let j = i + 1; if (j < text.length && '='.includes(text[j])) j++;
      tokens.push({ type: 'operator', value: text.substring(i, j) }); i = j; continue;
    }
    // Brackets/punctuation
    if ('(){}[],'.includes(text[i])) {
      tokens.push({ type: 'bracket', value: text[i] }); i++; continue;
    }
    // Whitespace or other
    tokens.push({ type: 'other', value: text[i] }); i++;
  }
  const colorMap = {
    function: 'text-indigo-400 font-semibold', keyword: 'text-purple-400 font-semibold', field: 'text-emerald-400',
    string: 'text-amber-400', number: 'text-cyan-400', operator: 'text-rose-300', bracket: 'text-zinc-400',
    identifier: 'text-zinc-100', other: 'text-zinc-300', reference: 'text-orange-400 font-semibold',
  };
  return tokens.map((t, idx) => <span key={idx} className={colorMap[t.type] || ''}>{t.value}</span>);
}

// Recursively expand {Measure} references into their (parenthesised) formulas —
// mirrors the backend formula_engine so the editor preview matches execution.
function expandFormulaRefs(text, measures, seen, depth) {
  if (!text || (depth || 0) > 25) return text || '';
  const byName = {};
  (measures || []).forEach(m => { if (m && m.name) byName[m.name.toLowerCase()] = m; });
  const cur = seen || new Set();
  return String(text).replace(/\{([^{}]+)\}/g, (full, raw) => {
    const key = raw.trim().toLowerCase();
    const t = byName[key];
    if (!t) return full;
    if (cur.has(key)) return 'NULL';
    if (!t.formula) return t.name || raw.trim();
    const inner = expandFormulaRefs(t.formula, measures, new Set([...cur, key]), (depth || 0) + 1);
    return `(${inner})`;
  });
}


export function FormulaBuilder({ formula, onChange, cursorPos, onCursorChange, fields, measures, onAiGenerate, submitLabel, onSubmit, submitDisabled, nameValue, onTest, children }) {
  const [activeCat, setActiveCat] = useState('Aggregation');
  const [autocomplete, setAutocomplete] = useState([]);
  const [acIndex, setAcIndex] = useState(0);
  const [testResult, setTestResult] = useState(null); // { ok: bool, message: str }
  const [testRun, setTestRun] = useState(null); // live test-against-data result
  const [testing, setTesting] = useState(false);
  const [refQuery, setRefQuery] = useState('');
  const [refOpen, setRefOpen] = useState(false);
  const [refActiveIdx, setRefActiveIdx] = useState(0);
  const [fnRefOpen, setFnRefOpen] = useState(false);
  const [fnRefQuery, setFnRefQuery] = useState('');
  const [fnResults, setFnResults] = useState({});

  useEffect(() => {
    if (fnRefOpen && Object.keys(fnResults).length === 0) {
      api.getFunctionReference().then((r) => setFnResults(r.results || {})).catch(() => {});
    }
  }, [fnRefOpen, fnResults]);
  const textareaRef = useRef(null);
  const activeFns = FORMULA_CATEGORIES.find(c => c.cat === activeCat)?.fns || [];
  const fieldNames = (fields || []).map(f => f.name);

  // Auto-grow the formula textarea to fit its content so it never shows a
  // vertical scrollbar. A scrollbar would shrink the textarea's text width by
  // the gutter while the (overflow-hidden) highlight overlay keeps full width,
  // making the two layers wrap at different points — the visible misalignment.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 64)}px`;
  }, [formula]);

  // Clear any prior test verdict whenever the formula changes.
  useEffect(() => { setTestResult(null); }, [formula]);

  const testFormula = useCallback(() => {
    const text = (formula || '').trim();
    if (!text) { setTestResult({ ok: false, message: 'Formula is empty.' }); return; }
    // 1) Balanced brackets
    const pairs = { ')': '(', ']': '[', '}': '{' };
    const stack = [];
    let inStr = null;
    for (let k = 0; k < text.length; k++) {
      const ch = text[k];
      if (inStr) { if (ch === inStr) inStr = null; continue; }
      if (ch === '"' || ch === "'") { inStr = ch; continue; }
      if (ch === '(' || ch === '[' || ch === '{') stack.push(ch);
      else if (pairs[ch]) { if (stack.pop() !== pairs[ch]) { setTestResult({ ok: false, message: `Unbalanced "${ch}" — check your brackets.` }); return; } }
    }
    if (inStr) { setTestResult({ ok: false, message: 'Unclosed string literal (missing quote).' }); return; }
    if (stack.length) { setTestResult({ ok: false, message: 'Unbalanced brackets — a "(" is not closed.' }); return; }
    // 2) Any word immediately followed by "(" must be a known function
    const fnNames = new Set(ALL_FUNCTIONS.map(f => f.name.toUpperCase()));
    const callRe = /([a-zA-Z_]\w*)\s*\(/g;
    let m;
    const unknown = [];
    while ((m = callRe.exec(text)) !== null) {
      const fn = m[1].toUpperCase();
      if (!fnNames.has(fn)) unknown.push(m[1]);
    }
    if (unknown.length) { setTestResult({ ok: false, message: `Unknown function${unknown.length > 1 ? 's' : ''}: ${[...new Set(unknown)].join(', ')}` }); return; }
    // 3) Must reference at least one field or function
    const usesFn = ALL_FUNCTIONS.some(f => new RegExp(`\\b${f.name}\\b`, 'i').test(text));
    const usesField = (fields || []).some(f => new RegExp(`\\b${f.name}\\b`, 'i').test(text));
    if (!usesFn && !usesField) { setTestResult({ ok: false, message: 'Formula doesn\u2019t reference any field or function yet.' }); return; }
    setTestResult({ ok: true, message: 'Formula looks valid \u2014 brackets balanced and functions recognized.' });
  }, [formula, fields]);

  const insertAtCursor = useCallback((text) => {
    const before = formula.substring(0, cursorPos);
    const after = formula.substring(cursorPos);
    onChange(before + text + after);
    onCursorChange(cursorPos + text.length);
  }, [formula, cursorPos, onChange, onCursorChange]);

  // Autocomplete: extract current word at cursor
  useEffect(() => {
    if (!formula || cursorPos === 0) { setAutocomplete([]); return; }
    const before = formula.substring(0, cursorPos);
    const match = before.match(/([a-zA-Z_]\w*)$/);
    if (!match || match[1].length < 2) { setAutocomplete([]); return; }
    const partial = match[1].toLowerCase();
    const suggestions = [
      ...(measures || []).filter(m => m.name && m.name.toLowerCase().includes(partial)).map(m => ({ label: m.name, desc: 'measure reference', name: m.name, type: 'ref' })),
      ...ALL_FUNCTIONS.filter(f => f.name.toLowerCase().startsWith(partial)).map(f => ({ label: f.name, desc: f.desc, usage: f.usage, type: 'fn' })),
      ...(fields || []).filter(f => f.name.toLowerCase().startsWith(partial)).map(f => ({ label: f.business_name || f.display_name || f.name, desc: f.name, usage: f.name, type: 'field' })),
    ].slice(0, 8);
    setAutocomplete(suggestions);
    setAcIndex(0);
  }, [formula, cursorPos, fields, measures]);

  const applyAutocomplete = (item) => {
    const before = formula.substring(0, cursorPos);
    const match = before.match(/([a-zA-Z_]\w*)$/);
    if (match) {
      const start = cursorPos - match[1].length;
      // Measure references insert as {Name}; don't double the leading brace if
      // the user already typed one.
      let insert = item.usage;
      if (item.type === 'ref') {
        const braceBefore = start > 0 && formula[start - 1] === '{';
        insert = (braceBefore ? '' : '{') + item.name + '}';
      }
      const newFormula = formula.substring(0, start) + insert + formula.substring(cursorPos);
      onChange(newFormula);
      onCursorChange(start + insert.length);
    }
    setAutocomplete([]);
  };

  const handleKeyDown = (e) => {
    if (autocomplete.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAcIndex(i => Math.min(i + 1, autocomplete.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setAcIndex(i => Math.max(i - 1, 0)); }
      else if (e.key === 'Tab' || e.key === 'Enter') {
        if (autocomplete[acIndex]) { e.preventDefault(); applyAutocomplete(autocomplete[acIndex]); }
      }
      else if (e.key === 'Escape') { setAutocomplete([]); }
    }
  };

  return (
    <div className="space-y-3" data-testid="formula-builder">
      {children}
      <div className="relative">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] text-muted-foreground">Formula</Label>
          <div className="flex items-center gap-3">
            <button onClick={testFormula} className="flex items-center gap-0.5 text-[9px] text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="formula-syntax-check-btn">
              <CheckCircle2 className="w-3 h-3" /><span>Check syntax</span>
            </button>
            {onAiGenerate && (
              <button onClick={onAiGenerate} className="flex items-center gap-0.5 text-[9px] text-indigo-400 hover:text-indigo-300 transition-colors" data-testid="formula-ai-btn">
                <Sparkles className="w-3 h-3" /><span>AI Generate</span>
              </button>
            )}
          </div>
        </div>
        {/* Syntax-highlighted preview overlay. The overlay and the textarea
            MUST share identical box metrics (font, padding, width, wrapping)
            so the colored text lines up exactly with the typed text. */}
        <div className="relative mt-0.5 rounded-lg bg-[#0d1117] border border-primary/30 focus-within:ring-1 focus-within:ring-primary/50">
          <div className="absolute inset-0 p-3 text-sm font-mono text-zinc-100 pointer-events-none overflow-hidden rounded-lg" aria-hidden="true"
            style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word', lineHeight: '1.5' }}>
            {highlightFormula(formula, fieldNames)}
          </div>
          <textarea
            ref={textareaRef}
            value={formula}
            onChange={e => { onChange(e.target.value); onCursorChange(e.target.selectionStart); }}
            onClick={e => onCursorChange(e.target.selectionStart)}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            placeholder="Click fields and operators below, or start typing for autocomplete..."
            className="w-full rounded-lg bg-transparent border-0 text-sm p-3 text-transparent caret-emerald-300 mt-0 min-h-[64px] font-mono focus:outline-none resize-none relative z-10 overflow-hidden"
            style={{ caretColor: '#6ee7b7', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word', lineHeight: '1.5' }}
            data-testid="formula-input"
          />
        </div>
        {/* Autocomplete dropdown */}
        {autocomplete.length > 0 && (
          <div className="absolute z-50 left-0 right-0 mt-0.5 rounded-lg border border-border/50 shadow-2xl overflow-hidden" style={{ background: '#0d1117' }} data-testid="formula-autocomplete">
            {autocomplete.map((item, i) => (
              <button key={item.label + i} onClick={() => applyAutocomplete(item)} className={`w-full text-left px-3 py-1.5 text-[11px] flex items-center gap-2 transition-colors ${i === acIndex ? 'bg-primary/30 text-white' : 'text-zinc-200 hover:bg-white/10'}`}>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.type === 'fn' ? 'bg-indigo-400' : item.type === 'ref' ? 'bg-orange-400' : 'bg-emerald-400'}`} />
                <span className="font-mono font-medium">{item.label}</span>
                <span className="text-zinc-400 ml-auto">{item.desc}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {(() => { const hint = describeFormula(formula); return hint ? (
        <div className="flex items-start gap-1.5 text-[10px] rounded-lg px-2.5 py-1.5 bg-sky-500/10 text-sky-700 dark:text-sky-300 border border-sky-500/30 dark:border-sky-500/20" data-testid="formula-live-hint">
          <Sparkles className="w-3 h-3 mt-px flex-shrink-0" />
          <span>{hint}</span>
        </div>
      ) : null; })()}

      {testResult && (
        <div className={`flex items-start gap-1.5 text-[10px] rounded-lg px-2.5 py-1.5 ${testResult.ok ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`} data-testid="formula-test-result">
          {testResult.ok ? <CheckCircle2 className="w-3 h-3 mt-px flex-shrink-0" /> : <AlertCircle className="w-3 h-3 mt-px flex-shrink-0" />}
          <span>{testResult.message}</span>
        </div>
      )}

      {/* Measure references — search and pick a formula to insert as {name} */}
      {(measures || []).length > 0 && (() => {
        const q = refQuery.trim().toLowerCase();
        const filtered = q
          ? (measures || []).filter(m => m.name && m.name.toLowerCase().includes(q))
          : (measures || []);
        return (
          <div className="relative">
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5">References to other formulas <span className="text-muted-foreground/70 normal-case">— insert as {'{name}'}</span></p>
            <input
              type="text"
              value={refQuery}
              onChange={(e) => { setRefQuery(e.target.value); setRefOpen(true); setRefActiveIdx(0); }}
              onFocus={() => { setRefOpen(true); setRefActiveIdx(0); }}
              onBlur={() => setTimeout(() => setRefOpen(false), 150)}
              onKeyDown={(e) => {
                if (!refOpen || filtered.length === 0) return;
                if (e.key === 'ArrowDown') { e.preventDefault(); setRefActiveIdx(i => Math.min(filtered.length - 1, i + 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setRefActiveIdx(i => Math.max(0, i - 1)); }
                else if (e.key === 'Enter') { e.preventDefault(); const m = filtered[Math.min(refActiveIdx, filtered.length - 1)]; if (m) { insertAtCursor(`{${m.name}}`); setRefQuery(''); setRefOpen(false); } }
                else if (e.key === 'Escape') { setRefOpen(false); }
              }}
              placeholder="Search a formula to insert… (↑/↓ then Enter)"
              title="Type to filter, arrow-keys to move, Enter to insert as {name}"
              data-testid="formula-references"
              className="w-full rounded-lg border border-border/50 bg-muted/50 px-2.5 py-1.5 text-[11px] text-foreground placeholder:text-muted-foreground/80 focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 transition-colors"
            />
            {refOpen && (
              <div className="absolute left-0 right-0 z-30 mt-1 max-h-40 overflow-auto rounded-lg border border-border/50 bg-popover shadow-xl" style={{ background: 'var(--glass-bg, rgba(15,20,40,0.98))', backdropFilter: 'blur(12px)' }} data-testid="formula-references-list">
                {filtered.length === 0 ? (
                  <p className="px-2.5 py-2 text-[10px] text-muted-foreground">No matching formulas</p>
                ) : filtered.map((m, idx) => (
                  <button
                    key={m.name}
                    type="button"
                    onMouseEnter={() => setRefActiveIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); insertAtCursor(`{${m.name}}`); setRefQuery(''); setRefOpen(false); }}
                    title={m.formula || m.name}
                    className={`w-full flex items-baseline gap-2 text-left px-2.5 py-1.5 text-[11px] border-b border-border/10 last:border-0 transition-colors ${idx === refActiveIdx ? 'bg-primary/15' : 'hover:bg-primary/10'}`}
                    data-testid={`formula-ref-${m.name}`}
                  >
                    <span className="font-mono text-foreground whitespace-nowrap">{'{'}{m.name}{'}'}</span>
                    {m.formula && <span className="font-mono text-muted-foreground/80 truncate">{m.formula}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      <div className="grid grid-cols-2 gap-3">
        {/* Fields */}
        <div className="col-span-1">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5">Fields</p>
          <div className="rounded-lg border border-border/30 max-h-36 overflow-auto">
            {(fields || []).map((a, ai) => (
              <button key={`${a.entityName || ''}.${a.name}.${ai}`} onClick={() => insertAtCursor(a.name)} title={a.description || a.name} className="w-full text-left px-2 py-1.5 text-[10px] hover:bg-primary/10 border-b border-border/10 last:border-0 transition-colors flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.semantic_type === 'measure' ? 'bg-primary' : 'bg-emerald-400'}`} />
                <span className="truncate">{a.display_name || a.label || a.business_name || a.name}</span>
                <span className="text-xs text-muted-foreground/70 ml-auto font-mono">{a.data_type}</span>
              </button>
            ))}
          </div>
        </div>
        {/* Functions */}
        <div className="col-span-1">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Functions</p>
            <button type="button" onClick={() => setFnRefOpen(true)} className="flex items-center gap-1 text-[9px] text-indigo-400 hover:text-indigo-300 transition-colors" data-testid="fn-reference-open"><BookOpen className="w-3 h-3" /> Reference</button>
          </div>
          <div className="rounded-lg border border-border/30 max-h-36 overflow-auto">
            <div className="flex flex-wrap gap-0.5 p-1 border-b border-border/20 sticky top-0 bg-background z-10">
              {FORMULA_CATEGORIES.map(c => (
                <button key={c.cat} onClick={() => setActiveCat(c.cat)} className={`px-1.5 py-0.5 rounded text-[8px] transition-colors ${activeCat === c.cat ? 'bg-indigo-500/20 text-indigo-400 font-medium' : 'text-muted-foreground/80 hover:text-foreground'}`}>{c.cat}</button>
              ))}
            </div>
            {activeFns.map(fn => (
              <button key={fn.name} onDoubleClick={() => { insertAtCursor(fn.usage); if (textareaRef.current) textareaRef.current.focus(); }} onClick={() => {}} className="w-full text-left px-2 py-1.5 hover:bg-indigo-500/10 border-b border-border/10 last:border-0 transition-colors" title={`Double-click to insert: ${fn.usage}`}>
                <span className="text-[10px] font-mono font-medium text-indigo-400">{fn.name}</span>
                <span className="text-xs text-muted-foreground/70 ml-1">{fn.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {fnRefOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="fn-reference-modal" onClick={() => setFnRefOpen(false)}>
          <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 p-3 border-b border-border/50">
              <BookOpen className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-semibold">Function reference</span>
              <button className="ml-auto text-muted-foreground hover:text-foreground" onClick={() => setFnRefOpen(false)} data-testid="fn-reference-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 border-b border-border/40">
              <input autoFocus value={fnRefQuery} onChange={(e) => setFnRefQuery(e.target.value)} placeholder="Search functions by name or description…" className="w-full bg-muted/30 rounded-lg px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-indigo-500/40" data-testid="fn-reference-search" />
            </div>
            <div className="overflow-auto p-2 space-y-1">
              {(() => {
                const fq = fnRefQuery.trim().toLowerCase();
                const seen = new Set();
                const list = ALL_FUNCTIONS.filter((f) => {
                  if (seen.has(f.name)) return false;
                  seen.add(f.name);
                  return !fq || f.name.toLowerCase().includes(fq) || (f.desc || '').toLowerCase().includes(fq);
                });
                if (!list.length) return <p className="text-xs text-muted-foreground px-2 py-6 text-center">No functions match “{fnRefQuery}”.</p>;
                return list.map((f) => {
                  const ex = fnResults[f.name];
                  return (
                    <div key={f.name} className="rounded-lg border border-border/30 p-2.5 hover:border-indigo-500/40 transition-colors" data-testid={`fn-ref-${f.name}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono font-semibold text-indigo-400">{f.name}</span>
                        <span className="text-[11px] text-muted-foreground">{f.desc}</span>
                        <button type="button" onClick={() => { insertAtCursor(f.usage); setFnRefOpen(false); if (textareaRef.current) textareaRef.current.focus(); }} className="ml-auto text-[10px] px-2 py-0.5 rounded border border-border/50 hover:bg-indigo-500/10 transition-colors" data-testid={`fn-ref-insert-${f.name}`}>Insert</button>
                      </div>
                      <code className="block mt-1 text-[11px] font-mono text-foreground/80">{f.usage}</code>
                      {ex && (
                        <div className="mt-1.5 flex items-center gap-2 text-[10px] flex-wrap">
                          <span className="font-mono text-muted-foreground">{ex.example}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 border border-emerald-500/20" data-testid={`fn-ref-result-${f.name}`}>{String(ex.result)}</span>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}


      {formula && nameValue !== undefined && (
        <div className="p-2.5 rounded-lg bg-muted/20 border border-border/30">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Preview</p>
          <p className="text-xs font-mono text-foreground break-words">{nameValue || 'unnamed'} = {highlightFormula(formula, fieldNames)}</p>
          {/\{[^{}]+\}/.test(formula) && (
            <div className="mt-1.5 pt-1.5 border-t border-border/20" data-testid="formula-expanded-preview">
              <p className="text-[9px] text-muted-foreground/70 uppercase tracking-wider mb-0.5">Expanded (references resolved)</p>
              <p className="text-xs font-mono text-muted-foreground break-words">{highlightFormula(expandFormulaRefs(formula, measures), fieldNames)}</p>
            </div>
          )}
          {onTest && (
            <div className="mt-1.5 pt-1.5 border-t border-border/20" data-testid="formula-test-block">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => { setTesting(true); setTestRun(null); try { const r = await onTest(formula, nameValue); setTestRun(r); } catch (e) { setTestRun({ ok: false, error: String(e?.message || e) }); } finally { setTesting(false); } }}
                  disabled={testing || !formula}
                  className="px-2.5 py-1 rounded-lg text-[10px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
                  data-testid="formula-test-btn"
                >
                  {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  {testing ? 'Testing…' : 'Test against data'}
                </button>
                <span className="text-[8px] text-muted-foreground/70">Runs the calculation on the model's real data</span>
              </div>
              {testRun && (
                <div className="mt-1.5" data-testid="formula-test-run-result">
                  {testRun.ok === false ? (
                    <div className="flex items-start gap-1.5 text-[10px] text-rose-400"><AlertCircle className="w-3 h-3 mt-px flex-shrink-0" /><span>{testRun.error || 'Calculation failed'}</span></div>
                  ) : (testRun.rows && testRun.rows.length ? (
                    <div className="rounded-lg border border-emerald-500/20 overflow-hidden">
                      <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 text-emerald-400 text-[9px]"><CheckCircle2 className="w-3 h-3" />Valid — {testRun.rows.length} sample record{testRun.rows.length > 1 ? 's' : ''}</div>
                      <table className="w-full text-[10px]">
                        <tbody>
                          {testRun.rows.map((r, i) => (
                            <tr key={i} className="border-t border-border/20">
                              {(testRun.columns || Object.keys(r)).map(c => (
                                <td key={c} className="px-2 py-0.5 font-mono text-foreground">{typeof r[c] === 'number' ? Number(r[c].toFixed(4)).toLocaleString() : String(r[c] ?? '—')}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1.5 text-[10px] text-emerald-400"><CheckCircle2 className="w-3 h-3 mt-px flex-shrink-0" /><span>Formula is valid (no sample rows returned).</span></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {onSubmit && (
        <Button onClick={onSubmit} disabled={submitDisabled} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="formula-submit-btn">
          <Plus className="w-4 h-4 mr-2" />{submitLabel || 'Create'}
        </Button>
      )}
    </div>
  );
}

export { FORMULA_CATEGORIES, ALL_FUNCTIONS, highlightFormula };
